import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Box, Grid, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'

import { TextInput, SelectInput, BasicTable, FileUploaderInput } from '../../../../ui'
import { COMPANY_FORM_CONFIG, INSPECTORES_LIST_CONFIG } from '../../constants'
import { CompanyLogo } from './company-logo.component'
import { IpIBANDisplayer } from './ip-iban-displayer.component'
import { PORequests } from './po-requests.component'
import { requests } from '../../../../core/ajax'
import { feedback } from '../../../../core/feedback'

const PRESCRIPTOR_INSTALADORES_CONFIG = [
  { field: 'nombre', title: 'Nombre' },
  { field: 'cif', title: 'CIF' },
  { field: 'email', title: 'Email' },
  { field: 'telefono', title: 'Teléfono' },
  { field: 'comunidad_autonoma', title: 'C. Autónoma' },
  {
    field: 'activo',
    title: 'Estado',
    render: (row) => <Chip size="small" label={row.activo ? 'Activo' : 'Inactivo'} />,
  },
]

export const CompanyForm = ({ model = {}, isPrimaryCompany = false, getEIData, onRefresh }) => {
  const intl = useIntl()
  const combos = useSelector((state) => state.combos)
  const [markingRead, setMarkingRead] = useState(false)

  const isPrescriptor = !!model.instaladores_prescriptor || model.tipo_actividad === 'prescriptor'
  const hasInstallerNotification = !!model.notificacion_instalador

  const handleMarkNotificationRead = () => {
    if (!model.id || markingRead) return
    setMarkingRead(true)
    requests
      .put(`/prescriptor/${model.id}/notificacion_instalador/revisado`)
      .then(() => {
        feedback('success', 'Notificación marcada como revisada')
        if (onRefresh) onRefresh()
      })
      .catch(() => {
        feedback('error', 'No se pudo marcar la notificación como revisada')
      })
      .finally(() => setMarkingRead(false))
  }

  const showLeftContent = (model.captadora || !isPrimaryCompany) && model.publica && !model.logo
  const formState = {
    ...model,
    acepta_financiacion: model['acepta_financiacion'] ? '01' : '00',
    publica: model['publica'] ? '01' : '00',
  }
  const inputProps = { formState, combos, intl }

  return (
    <form>
      <Box p={3} display="flex">
        <Grid container spacing={3}>
          {showLeftContent ? (
            <Grid size={{ sm: 12, md: 4 }}>
              <CompanyLogo
                {...{
                  hasPublicProfile: formState.publica,
                  isPrimaryCompany,
                  model: formState,
                }}
              />
            </Grid>
          ) : null}
          <Grid size={{ sm: 12, md: showLeftContent ? 8 : 12 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).NOMBRE} />
              </Grid>
              {isPrimaryCompany ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextInput {...COMPANY_FORM_CONFIG(inputProps).NIF} />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).RAZON} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).WEB} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).DIRECCION} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).COD_POSTAL} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).EMAIL} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextInput {...COMPANY_FORM_CONFIG(inputProps).TELEFONO} />
              </Grid>
              {formState.captadora || !isPrimaryCompany ? (
                <>
                  {isPrimaryCompany ? (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <TextInput {...COMPANY_FORM_CONFIG(inputProps).PUBLICA} />
                    </Grid>
                  ) : null}
                  {isPrimaryCompany && formState.publica === '01' ? (
                    <>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <SelectInput {...COMPANY_FORM_CONFIG(inputProps).ACEPTA_F} />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <TextInput {...COMPANY_FORM_CONFIG(inputProps).COD_ENTIDAD_FINANCIERA} />
                      </Grid>
                    </>
                  ) : null}
                  <Grid size={12}>
                    <TextInput {...COMPANY_FORM_CONFIG(inputProps).DESCRIPCION} />
                  </Grid>
                </>
              ) : null}
              {isPrimaryCompany && formState.grabadora ? (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                    }}
                  >
                    <SelectInput {...COMPANY_FORM_CONFIG(inputProps).CATEGORIA} />
                  </Grid>
                  <Grid size={12}>
                    <IpIBANDisplayer {...{ inputProps, getEIData }} />
                  </Grid>
                </>
              ) : null}
              {formState.captadora || !isPrimaryCompany ? (
                <>
                  <Grid style={{ margin: '15px 0 10px 0' }} size={12}>
                    <Typography variant="h6" color="primary">
                      Datos de Registro para Oferta Pública
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                    }}
                  >
                    <TextInput {...COMPANY_FORM_CONFIG(inputProps).RESPONSABLE_LEGAL} />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                    }}
                  >
                    <TextInput {...COMPANY_FORM_CONFIG(inputProps).NIF_RESPONSABLE_LEGAL} />
                  </Grid>
                  {isPrimaryCompany ? (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <TextInput {...COMPANY_FORM_CONFIG(inputProps).CODIGO_REG_INDUSTRIAL} />
                    </Grid>
                  ) : null}
                  {!isPrimaryCompany ? (
                    <>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <TextInput {...COMPANY_FORM_CONFIG(inputProps).IBAN_OP} />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <FileUploaderInput
                          {...COMPANY_FORM_CONFIG(inputProps).CERTIFICADO_BANCARIO_IBAN_SWIFT}
                        />
                      </Grid>
                    </>
                  ) : null}
                </>
              ) : null}
              <Grid style={{ margin: '15px 0 10px 0' }} size={12}>
                {!isPrimaryCompany ? <PORequests data={formState.ofertas_publicas} /> : null}
              </Grid>
              {isPrimaryCompany && formState.inspectores?.length ? (
                <Grid size={12}>
                  <Typography variant="h6" paragraph>
                    Listado de instaladores
                  </Typography>
                  <BasicTable config={INSPECTORES_LIST_CONFIG} data={formState.inspectores} />
                </Grid>
              ) : null}
              {isPrimaryCompany && isPrescriptor ? (
                <Grid size={12}>
                  <Box display="flex" alignItems="center" mb={1} gap={1}>
                    <Typography variant="h6">Instaladores colaboradores</Typography>
                    <Tooltip
                      title={
                        hasInstallerNotification
                          ? 'Hay cambios pendientes de revisar. Haz clic para marcar como revisado.'
                          : 'Sin notificaciones pendientes'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color={hasInstallerNotification ? 'secondary' : 'default'}
                          onClick={
                            hasInstallerNotification ? handleMarkNotificationRead : undefined
                          }
                          disabled={markingRead || !hasInstallerNotification}
                        >
                          {hasInstallerNotification ? (
                            <NotificationsActiveIcon fontSize="small" />
                          ) : (
                            <NotificationsNoneIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {formState.instaladores_prescriptor?.length ? (
                    <BasicTable
                      config={PRESCRIPTOR_INSTALADORES_CONFIG}
                      data={formState.instaladores_prescriptor}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No hay instaladores colaboradores registrados.
                    </Typography>
                  )}
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </form>
  )
}
